import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NWClient } from '../../client/NWClient';
import { DocumentHtmlDataModel } from '../../models/document.html.data.model';
import { revertAll, RootState } from '../../store';

export interface FetchDocumentHTMLRequest {
  htmlArray: number[];
  useLatestVersion: boolean;
}

export interface SaveDocumentHTMLRequest {
  documentId: string;
  html: string;
  isContractGenerated: boolean;
  userId: number;
}

export const fetchDocumentHTML = createAsyncThunk(
  'documentHTML/fetch',
  async ({ htmlArray, useLatestVersion }: FetchDocumentHTMLRequest) => {
    const htmlId = useLatestVersion ? Math.max(...htmlArray) : Math.min(...htmlArray);
    const response = await NWClient.get('document-upload-html-data', htmlId);
    return response as DocumentHtmlDataModel;
  }
);

export const saveDocumentHTML = createAsyncThunk(
  'documentHTML/save',
  async ({ documentId, html, isContractGenerated, userId }: SaveDocumentHTMLRequest) => {
    if (isContractGenerated) {
      const data = { user: userId, html };
      const response = await NWClient.patch(`document-contract/${documentId}/`, null, data);
      return response;
    } else {
      const data = { html, document_upload: Number(documentId) };
      const response = await NWClient.post('document-upload-html', data);
      return response;
    }
  }
);

export interface DocumentHTMLState {
  html: string | null;
  contentEditable: boolean;
  showConfirmationModal: boolean;
  fetchStatus: string;
  saveStatus: string;
  error: string;
}

const initialState: DocumentHTMLState = {
  html: null,
  contentEditable: false,
  showConfirmationModal: false,
  fetchStatus: 'idle',
  saveStatus: 'idle',
  error: '',
};

const documentHTMLSlice = createSlice({
  name: 'documentHTML',
  initialState,
  reducers: {
    setDocumentHTML(state, action: PayloadAction<string>) {
      state.html = action.payload;
    },
    setContentEditable(state, action: PayloadAction<boolean>) {
      state.contentEditable = action.payload;
    },
    setShowConfirmationModal(state, action: PayloadAction<boolean>) {
      state.showConfirmationModal = action.payload;
    },
    resetDocumentHTML() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchDocumentHTML.pending, (state) => {
        state.fetchStatus = 'loading';
        state.error = '';
      })
      .addCase(fetchDocumentHTML.fulfilled, (state, action) => {
        state.fetchStatus = 'succeeded';
        state.html = action.payload.html;
      })
      .addCase(fetchDocumentHTML.rejected, (state, action) => {
        state.fetchStatus = 'failed';
        state.error = action.error.message ?? '';
      })
      .addCase(saveDocumentHTML.pending, (state) => {
        state.saveStatus = 'loading';
        state.error = '';
      })
      .addCase(saveDocumentHTML.fulfilled, (state) => {
        state.saveStatus = 'succeeded';
      })
      .addCase(saveDocumentHTML.rejected, (state, action) => {
        state.saveStatus = 'failed';
        state.error = action.error.message ?? '';
      })
      .addCase(revertAll, () => initialState);
  },
});

export const { setDocumentHTML, setContentEditable, setShowConfirmationModal, resetDocumentHTML } =
  documentHTMLSlice.actions;

export default documentHTMLSlice.reducer;

export const selectDocumentHTML = (state: RootState) => state.documentHTML.html;
export const selectDocumentHTMLContentEditable = (state: RootState) =>
  state.documentHTML.contentEditable;
export const selectDocumentHTMLShowConfirmationModal = (state: RootState) =>
  state.documentHTML.showConfirmationModal;
export const selectDocumentHTMLFetchStatus = (state: RootState) => state.documentHTML.fetchStatus;
export const selectDocumentHTMLSaveStatus = (state: RootState) => state.documentHTML.saveStatus;
export const selectDocumentHTMLError = (state: RootState) => state.documentHTML.error;
